import React, { useContext, useState } from 'react';
import { CartContext } from '../../../context/CartContext';
import { ChevronDown, ChevronUp, Plus, Minus, Edit, Trash2, AlertCircle, Package, Clock, MessageSquare, ChefHat, Pizza } from 'lucide-react';
import SpecialInstructionsModal from '../Modals/SpecialInstructionsModal';
import OrderNotesModal from '../Modals/OrderNotesModal';
import ToppingModal from '../Modals/ToppingModal';
import { useMenuItems } from '../../../hooks/useMenuItems';
import toast from 'react-hot-toast';

export default function DraftKOT() {
  const {
    getCurrentCartItems,
    getCurrentOrder,
    updateQuantity,
    removeFromCart,
    updateItemInstructions,
    updateOrderNotes,
    addToCart,
  } = useContext(CartContext);

  const { items: menuItems, loading: menuItemsLoading } = useMenuItems();

  const toppingItems = (menuItems || []).filter((item) => item.markedAsToppings);

  const [isExpanded, setIsExpanded] = useState(true);

  // Special Instructions Modal State
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Order Notes Modal State
  const [showOrderNotesModal, setShowOrderNotesModal] = useState(false);

  // Topping Modal State — now scoped to a specific parent item
  const [showToppingModal, setShowToppingModal] = useState(false);
  const [toppingTargetItem, setToppingTargetItem] = useState(null);

  const currentOrder = getCurrentOrder();
  const cartItems = getCurrentCartItems() || [];

  // Get only items NOT sent to kitchen (draft items)
  const draftItems = cartItems.filter(item => !item.sentToKitchen);

  // Top-level draft items = anything that isn't itself a topping linked to a parent
  const parentDraftItems = draftItems.filter(item => !item.customization?.parentCartId);

  // Toppings linked to a given parent cartId
  const getLinkedToppings = (parentCartId) =>
    draftItems.filter(item => item.customization?.parentCartId === parentCartId);

  const getStationBadgeColor = (station) => {
    const normalizedStation = station?.toLowerCase()?.trim() || '';
    const colors = {
      'main kitchen': 'bg-blue-50 text-blue-600 border-blue-200',
      'kitchen': 'bg-blue-50 text-blue-600 border-blue-200',
      'tandoor': 'bg-orange-50 text-orange-600 border-orange-200',
      'grill': 'bg-red-50 text-red-600 border-red-200',
      'bar': 'bg-purple-50 text-purple-600 border-purple-200',
      'bakery': 'bg-pink-50 text-pink-600 border-pink-200',
      'cold kitchen': 'bg-cyan-50 text-cyan-600 border-cyan-200',
      'hot kitchen': 'bg-red-50 text-red-600 border-red-200',
    };
    return colors[normalizedStation] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  const handleIncreaseQuantity = (cartId, currentQuantity) => {
    updateQuantity(cartId, currentQuantity + 1);
  };

  const handleDecreaseQuantity = (cartId, currentQuantity) => {
    updateQuantity(cartId, currentQuantity - 1);
  };

  const handleRemoveItem = (cartId) => {
    removeFromCart(cartId, 'Removed from draft');
  };

  // Handle special instructions
  const handleAddInstructions = (item) => {
    setSelectedItem(item);
    setShowInstructionsModal(true);
  };

  const handleSaveInstructions = (instructions) => {
    if (selectedItem) {
      updateItemInstructions(selectedItem.cartId, instructions);
    }
    setShowInstructionsModal(false);
    setSelectedItem(null);
  };

  // Handle order notes
  const handleSaveOrderNotes = (notes) => {
    updateOrderNotes({
      ...currentOrder?.orderNotes,
      chefNotes: notes
    });
    setShowOrderNotesModal(false);
  };

  // Open topping picker for a specific item
  const handleOpenToppingModal = (item) => {
    setToppingTargetItem(item);
    setShowToppingModal(true);
  };

  // Handle adding a topping, linked to the item it was opened from
  const handleAddTopping = (toppingItem) => {
    if (!toppingTargetItem) return;

    addToCart(
      { ...toppingItem, price: toppingItem.price ?? toppingItem.basePrice },
      {
        customInstructions: '',
        parentCartId: toppingTargetItem.cartId,
        parentItemName: toppingTargetItem.name,
      },
      true
    );
  };

  const handleCloseToppingModal = () => {
    setShowToppingModal(false);
    setToppingTargetItem(null);
  };

  // Don't show if no draft items
  if (draftItems.length === 0) {
    return null;
  }

  const totalDraftItems = draftItems.reduce((sum, item) => sum + item.quantity, 0);

  const renderItemRow = (item, isTopping = false) => (
    <div
      key={item.cartId}
      className={`flex items-center gap-3 px-4 py-2 hover:bg-amber-50 transition-colors ${
        isTopping ? 'bg-pink-50/40 pl-8 border-l-2 border-pink-200 ml-4' : ''
      }`}
    >
      {/* Name + inline badges + instructions */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isTopping && (
            <Pizza size={12} className="text-pink-600 flex-shrink-0" />
          )}
          <h4 className={`font-semibold text-slate-900 truncate ${isTopping ? 'text-xs' : 'text-sm'}`}>
            {isTopping && '+ '}{item.name}
          </h4>
          {isTopping && (
            <span className="text-[10px] bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded border border-pink-200 font-medium uppercase tracking-wide">
              Topping
            </span>
          )}
          {item.customization?.size && (
            <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 font-medium">
              {item.customization.size}
            </span>
          )}
          {item.customization?.spiceLevel && (
            <span className="text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200 font-medium">
              {item.customization.spiceLevel}
            </span>
          )}
          {item.customization?.preparation && (
            <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 font-medium">
              {item.customization.preparation}
            </span>
          )}
          {item.kitchenStationName && (
            <span className={`text-[9px] px-1 py-0.5 rounded border font-medium ${getStationBadgeColor(item.kitchenStationName)}`}>
              {item.kitchenStationName}
            </span>
          )}
          {item.customization?.quickNotes?.map((note, i) => (
            <span key={i} className="text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded border border-teal-200 font-medium">
              {note}
            </span>
          ))}
        </div>

        {item.customization?.customInstructions && (
          <p className="text-[11px] text-amber-700 italic truncate mt-0.5">
            💬 {item.customization.customInstructions}
          </p>
        )}
      </div>

      {/* Quantity Controls */}
      <div className="flex items-center gap-1 bg-amber-50 rounded-lg px-1 py-0.5 border border-amber-300 shrink-0">
        <button
          onClick={() => handleDecreaseQuantity(item.cartId, item.quantity)}
          className="p-1 hover:bg-amber-100 rounded transition font-bold text-amber-700"
          title="Decrease quantity"
        >
          <Minus size={12} />
        </button>
        <span className="w-5 text-center font-black text-slate-900 text-sm">
          {item.quantity}
        </span>
        <button
          onClick={() => handleIncreaseQuantity(item.cartId, item.quantity)}
          className="p-1 hover:bg-amber-100 rounded transition font-bold text-amber-700"
          title="Increase quantity"
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Add Note Button */}
      <button
        onClick={() => handleAddInstructions(item)}
        className={`p-1 rounded-lg transition shrink-0 ${
          item.customization?.customInstructions
            ? 'text-amber-700 bg-amber-100 hover:bg-amber-200'
            : 'text-slate-400 hover:bg-slate-100'
        }`}
        title={
          item.customization?.customInstructions
            ? 'Edit special instructions'
            : 'Add special instructions'
        }
      >
        <MessageSquare size={14} />
      </button>

      {/* Add Topping Button — only on non-topping (parent) items */}
      {!isTopping && (
        <button
          onClick={() => handleOpenToppingModal(item)}
          className="p-1 rounded-lg transition shrink-0 text-slate-400 hover:bg-pink-50 hover:text-pink-600"
          title="Add topping to this item"
        >
          <Pizza size={14} />
        </button>
      )}

      {/* Remove Button */}
      <button
        onClick={() => handleRemoveItem(item.cartId)}
        className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition shrink-0"
        title="Remove from draft"
      >
        <Trash2 size={14} />
      </button>

      {/* Price */}
      <div className="text-right shrink-0 w-16">
        <p className={`font-bold text-amber-700 ${isTopping ? 'text-xs' : 'text-sm'}`}>
          ₹{((item.finalPrice || item.price) * item.quantity).toFixed(0)}
        </p>
      </div>
    </div>
  );

  return (
    <div className="mb-4">
      <div className="bg-amber-50 border-2 border-amber-300 rounded-lg shadow-lg overflow-hidden">
        {/* Draft KOT Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 bg-gradient-to-r from-amber-100 to-amber-50 hover:from-amber-150 hover:to-amber-100 transition-colors text-left border-b-2 border-amber-300"
        >
          {/* Line 1: Draft Label + Warning + Order Notes */}
          <div className="flex items-center gap-2 mb-2">
            <Package size={16} className="text-amber-700 flex-shrink-0" />
            <span className="text-sm font-bold text-amber-900 uppercase tracking-wide">
              Draft KOT
            </span>
            <span className="text-amber-600 text-xs font-medium">
              (Not sent to kitchen)
            </span>

            {/* Order Notes Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowOrderNotesModal(true);
              }}
              className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all font-semibold shadow-sm ${
                currentOrder?.orderNotes?.chefNotes
                  ? 'bg-teal-500 text-white hover:bg-teal-600 ring-2 ring-teal-300'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border-2 border-dashed border-amber-400 hover:border-amber-500'
              }`}
              title={currentOrder?.orderNotes?.chefNotes ? 'Edit order notes' : 'Add order notes'}
            >
              <ChefHat size={14} className={currentOrder?.orderNotes?.chefNotes ? '' : 'text-amber-600'} />
              <MessageSquare size={12} className={currentOrder?.orderNotes?.chefNotes ? '' : 'text-amber-600'} />
              {!currentOrder?.orderNotes?.chefNotes && (
                <>
                  <span className="text-[10px] uppercase tracking-wide">Notes</span>
                  <div className="absolute -top-1 -right-1">
                    <div className="relative flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                      <div className="absolute w-2 h-2 rounded-full bg-orange-400 animate-ping"></div>
                    </div>
                  </div>
                </>
              )}
              {currentOrder?.orderNotes?.chefNotes && (
                <span className="text-[10px] uppercase tracking-wide">Edit</span>
              )}
            </button>

            <div className="ml-auto">
              {isExpanded ? (
                <ChevronUp size={18} className="text-amber-700" />
              ) : (
                <ChevronDown size={18} className="text-amber-700" />
              )}
            </div>
          </div>

          {/* Line 2: Items Count */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex items-center">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></div>
              <div className="absolute w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping"></div>
            </div>
            <span className="text-sm font-bold text-amber-900">
              {totalDraftItems} {totalDraftItems === 1 ? 'item' : 'items'} pending
            </span>
          </div>
        </button>

        {/* Expanded Draft Details */}
        {isExpanded && (
          <div className="bg-white border-t border-amber-200">
            {/* Instructions Banner */}
            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <AlertCircle size={14} className="text-amber-700 flex-shrink-0" />
              <p className="text-xs text-amber-800 font-medium">
                Click <strong>"Send to Kitchen (KOT)"</strong> button to confirm these items
              </p>
            </div>

            {/* Draft Items List — parents with nested toppings */}
            <div className="divide-y divide-amber-100">
              {parentDraftItems.map((item) => {
                const linkedToppings = getLinkedToppings(item.cartId);
                return (
                  <div key={item.cartId}>
                    {renderItemRow(item, false)}
                    {linkedToppings.map((topping) => renderItemRow(topping, true))}
                  </div>
                );
              })}
            </div>

            {/* Chef Notes from Order */}
            {currentOrder?.orderNotes?.chefNotes && (
              <div className="mx-4 mb-4 mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <span className="text-lg">👨‍🍳</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-900 uppercase mb-0.5">
                      Chef Notes (will be included)
                    </p>
                    <p className="text-sm text-amber-900">{currentOrder.orderNotes.chefNotes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Special Instructions Modal */}
      {showInstructionsModal && selectedItem && (
        <SpecialInstructionsModal
          item={selectedItem}
          onSave={handleSaveInstructions}
          onClose={() => {
            setShowInstructionsModal(false);
            setSelectedItem(null);
          }}
        />
      )}

      {/* Order Notes Modal */}
      {showOrderNotesModal && (
        <OrderNotesModal
          currentNotes={currentOrder?.orderNotes?.chefNotes || ''}
          onSave={handleSaveOrderNotes}
          onClose={() => setShowOrderNotesModal(false)}
        />
      )}

      {/* Topping Modal — scoped to toppingTargetItem */}
      {showToppingModal && (
        <ToppingModal
          toppingItems={toppingItems}
          loading={menuItemsLoading}
          parentItemName={toppingTargetItem?.name}
          onAdd={handleAddTopping}
          onClose={handleCloseToppingModal}
        />
      )}
    </div>
  );
}